import { useEffect } from 'react';
import { startCase } from 'lodash';
import useStores from 'shared/hooks/use-stores';
import { PAY_ONLINE_MERRCO, PAY_ONLINE_MONERIS } from 'src/payments/constants';
import { useCheckout } from 'src/checkout/context';
import { WhiteLabelConfig } from 'src/payments/hooks';
import { useIsFirstRender } from 'src/hooks/use-is-first-render';
import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { DispensaryPaymentOptions, getCardType } from 'src/checkout/components/sections/payment/shared/utils';
import { usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';

export type UseOnlineOptionsResponse = {
  orderTypeUsingPayByBank: boolean;
  hasOtherOrderTypesWithPBBEnabled: boolean;
  showCreditCardInformation: boolean;
  creditCardError: string;
  idRequiredText: string;
  paymentMethod: string;
  merrcoPublicToken?: string;
  isKioskOrder: boolean;
  showSaveButton: boolean;
  handleSetPaysafeToken: (token: string, zip: string, cardType: string) => void;
  handleSaveMonerisPayment: () => void;
  saveOnlinePaymentMethod: () => void;
  setCreditCardError: (error: string | null) => void;
  onlinePaymentOption: DispensaryPaymentOptions[number] | null;
  whiteLabelConfig: WhiteLabelConfig;
  showBankInformation: boolean;
  hasOnlineAndOfflinePaymentOptions: boolean;
};

export const useOnlineOptions = (): UseOnlineOptionsResponse => {
  const {
    showBankInformation,
    onlinePaymentOptionValue,
    paymentMethod,
    onlinePaymentOptionIsDutchiePay,
    paymentMethodIsDutchiePay,
    hasOtherOrderTypesWithPBBEnabled,
    showCreditCardInformation,
    hasOnlineAndOfflinePaymentOptions,
    isEnrolledDutchiePay,
    onlinePaymentOption,
    whiteLabelConfig,
  } = useCartPayment();

  const { setExpanded, creditCardError, setPaymentCardType, setCreditCardError } = usePaymentSelectionContext();
  const { Cart } = useStores();
  const Checkout = useCheckout();
  const isFirstRender = useIsFirstRender();
  const { order, isKioskOrder } = Cart ?? {};

  const { dispensary, deliveryOption } = order ?? {};
  const { merrcoPublicToken = '' } = dispensary ?? {};
  const idRequiredText = `You’ll be required to show your ID and credit card upon ${
    deliveryOption === 'delivery' ? 'delivery' : 'pickup'
  }.`;
  const paymentError = Checkout.getError({ path: 'payment' }).error;

  const orderTypeUsingPayByBank = onlinePaymentOptionIsDutchiePay && paymentMethodIsDutchiePay;

  const showSaveButton = paymentMethodIsDutchiePay && onlinePaymentOptionIsDutchiePay && isEnrolledDutchiePay;

  // Clear existing cart tokens for merrco / moneris on first render if they exist
  useEffect(() => {
    if (!isFirstRender) {
      return;
    }

    const clearCartInputs = {
      [PAY_ONLINE_MERRCO]: () => (Cart.paysafeInput = { token: null, zip: null }),
      [PAY_ONLINE_MONERIS]: () => (Cart.monerisInput = {}),
    };

    clearCartInputs[onlinePaymentOptionValue]?.();
  }, [onlinePaymentOptionValue, Cart, isFirstRender]);

  useEffect(() => {
    if (showCreditCardInformation && paymentError) {
      setCreditCardError(paymentError.message);
      setExpanded(true);
    }
  }, [setExpanded, setCreditCardError, paymentError, showCreditCardInformation]);

  // Event Handlers
  const handleSetPaysafeToken = (token: string, zip: string, cardType: string): void => {
    Cart.paysafeInput = { token, zip };
    setPaymentCardType(startCase(cardType));
    saveOnlinePaymentMethod();
  };

  const handleSaveMonerisPayment = (): void => {
    const binNumber = Cart.monerisInput.bin ?? '';
    const cardType = startCase(getCardType(binNumber));
    setPaymentCardType(cardType);
    saveOnlinePaymentMethod();
  };
  // Event Handlers
  const saveOnlinePaymentMethod = (): void => {
    setExpanded(false);
    Checkout.removeError({ match: 'payment' });
  };

  return {
    orderTypeUsingPayByBank,
    hasOtherOrderTypesWithPBBEnabled,
    showCreditCardInformation,
    creditCardError,
    idRequiredText,
    paymentMethod,
    merrcoPublicToken,
    isKioskOrder,
    showSaveButton,
    handleSetPaysafeToken,
    handleSaveMonerisPayment,
    saveOnlinePaymentMethod,
    setCreditCardError,
    onlinePaymentOption,
    whiteLabelConfig,
    showBankInformation,
    hasOnlineAndOfflinePaymentOptions,
  };
};
